import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Clock, PlayCircle, Eye, CheckCircle2, Boxes, PackageCheck, Users as UsersIcon, Sparkles, AlertTriangle, Trophy, CalendarDays, MapPin, FileText } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

function CreativeReportButton({ t }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: monthAgo, to: today });

  const open = () => {
    const auth = localStorage.getItem("access_token") || "";
    window.open(`${api.defaults.baseURL}/reports/creative-performance?date_from=${range.from}&date_to=${range.to}&auth=${auth}`, "_blank", "noopener");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" data-testid="creative-report-btn" className="border-white/10 bg-white/5 hover:bg-white/10">
          <FileText className="w-4 h-4 mr-1.5" />{t("creative_report")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="bg-slate-900 border-white/10 w-72 space-y-3" data-testid="creative-report-popover">
        <div className="text-xs uppercase tracking-widest text-slate-400">{t("period")}</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-400">{t("from")}</Label>
            <Input data-testid="report-from-input" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">{t("to")}</Label>
            <Input data-testid="report-to-input" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
          </div>
        </div>
        <Button size="sm" data-testid="report-generate-btn" onClick={open} disabled={range.from > range.to} className="w-full bg-indigo-500 hover:bg-indigo-600">
          {t("download_pdf")}
        </Button>
        {range.from > range.to && <p data-testid="report-range-error" className="text-xs text-rose-300">{t("range_invalid")}</p>}
      </PopoverContent>
    </Popover>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="kpi-card" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
          <div className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tint }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/orders").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.status === "upcoming" || e.status === "ongoing").slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            {t("welcome_back")}, {user?.name}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
            {t("dashboard")}
          </h1>
          <p className="text-slate-400 mt-2 max-w-lg">{t("tagline")}</p>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">{t("orders")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Clock} label={t("kpi_pending")} value={stats?.orders.pending} tint="rgba(245,158,11,0.15)" />
          <Kpi icon={PlayCircle} label={t("kpi_in_progress")} value={stats?.orders.in_progress} tint="rgba(59,130,246,0.15)" />
          <Kpi icon={Eye} label={t("kpi_review")} value={stats?.orders.review} tint="rgba(139,92,246,0.15)" />
          <Kpi icon={CheckCircle2} label={t("kpi_completed")} value={stats?.orders.completed} tint="rgba(16,185,129,0.15)" />
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">{t("equipment")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Boxes} label={t("kpi_types")} value={stats?.equipment.types} tint="rgba(99,102,241,0.15)" />
          <Kpi icon={PackageCheck} label={t("kpi_available")} value={stats?.equipment.available_units} tint="rgba(16,185,129,0.15)" />
          <Kpi icon={ClipboardList} label={t("kpi_equipment_out")} value={stats?.equipment.checked_out} tint="rgba(239,68,68,0.15)" />
          <Kpi icon={UsersIcon} label={t("kpi_team")} value={stats?.users_total} tint="rgba(236,72,153,0.15)" />
        </div>
        {stats?.overdue > 0 && (
          <div data-testid="dashboard-overdue-alert" className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-center gap-3 text-rose-200">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">{stats.overdue}</span> {t("overdue_banner")}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="glass rounded-2xl p-6" data-testid="team-stats-card">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Trophy className="w-5 h-5 text-amber-300 shrink-0" />{t("team_stats")}</h2>
            <p className="text-xs text-slate-400">{t("team_stats_hint")} · {stats?.month}</p>
          </div>
          {["admin", "member"].includes(user?.role) && <CreativeReportButton t={t} />}
        </div>
        {!stats?.team_month?.length ? (
          <div className="text-sm text-slate-500 py-6 text-center">{t("no_data")}</div>
        ) : (
          <div className="space-y-3">
            {stats.team_month.map((m, i) => {
              const max = Math.max(1, ...stats.team_month.map((x) => x.output ?? x.completed));
              return (
                <div key={m.user_id} data-testid={`team-row-${m.user_id}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-5 text-xs ${i === 0 ? "text-amber-300 font-bold" : "text-slate-500"}`}>#{i + 1}</span>
                      <span className="font-medium truncate">{m.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">{t(m.role)}</span>
                    </div>
                    <div className="text-xs text-slate-300 whitespace-nowrap"><span className="font-bold text-emerald-300">{m.output ?? m.completed}</span> {t("output_label")} · {m.completed} {t("completed").toLowerCase()} · {m.reports ?? 0} {t("reports_count")} · {m.active} {t("in_progress").toLowerCase()}</div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-400/70 transition-all" style={{ width: `${((m.output ?? m.completed) / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6" data-testid="upcoming-events-card">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><CalendarDays className="w-5 h-5 text-indigo-300 shrink-0" />{t("upcoming_events")}</h2>
            <p className="text-xs text-slate-400">{t("upcoming_events_hint")}</p>
          </div>
          <Link to="/events" data-testid="dashboard-events-link" className="text-xs text-indigo-300 hover:text-indigo-200 whitespace-nowrap">{t("view_all")} →</Link>
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">{t("no_data")}</div>
        ) : (
          <div className="space-y-4">
            {events.map((e) => {
              const pct = e.checklist_total ? Math.round((e.checklist_done / e.checklist_total) * 100) : 0;
              return (
                <Link key={e.id} to={`/events?open=${e.id}`} data-testid={`dashboard-event-${e.id}`} className="block rounded-xl p-3 -mx-1 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="font-medium truncate">{e.name}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border whitespace-nowrap ${e.status === "ongoing" ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-indigo-400/30 bg-indigo-400/10 text-indigo-200"}`}>{t(`ev_${e.status}`)}</span>
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-3 flex-wrap mb-2">
                    <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{e.start_date}{e.end_date !== e.start_date ? ` → ${e.end_date}` : ""}</span>
                    {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                    <span>{e.orders_count} {t("orders").toLowerCase()}</span>
                  </div>
                  {e.checklist_total > 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full bg-indigo-400/70 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-400 whitespace-nowrap" data-testid={`dashboard-event-progress-${e.id}`}>{e.checklist_done}/{e.checklist_total} · {pct}%</span>
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500" data-testid={`dashboard-event-progress-${e.id}`}>{t("no_checklist")}</div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{t("orders")}</h2>
            <p className="text-xs text-slate-400">Recent</p>
          </div>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">{t("no_data")}</div>
        ) : (
          <div className="divide-y divide-white/5">
            {recent.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.title}</div>
                  <div className="text-xs text-slate-400">
                    {t(o.type)} · {t(o.priority)} · {o.created_by_name}
                  </div>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full border border-white/10 bg-white/5">
                  {t(o.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
